import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  /** タイトル横のバッジ（source_type・非表示など） */
  badges?: React.ReactNode;
  /** ヘッダー右のアクション */
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  /**
   * ドロワー幅。数値は「最小幅(px)」として扱い、実際は画面幅の約60%まで広がる（上限あり）。
   * 文字列を渡した場合はその値をそのまま幅に使う。
   */
  width?: number | string;
};

/**
 * AWSコンソール風の詳細ドロワー（右からスライドイン）。
 * リソース行を選択すると開き、詳細・編集・関連リソースを表示する。
 */
const ConsoleDetailDrawer: React.FC<Props> = ({
  open,
  onClose,
  title,
  badges,
  headerActions,
  children,
  width = 620,
}) => {
  // 数値は最小幅(px)として扱い、画面幅の約60%まで広げる（上限 1100px）。
  // 文字列指定時はその値をそのまま使う。
  const paperWidth = typeof width === 'number' ? `clamp(${width}px, 60vw, 1100px)` : width;
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            width: { xs: '100%', sm: paperWidth },
            maxWidth: '100vw',
            borderLeft: 1,
            borderColor: 'divider',
            backgroundImage: 'none',
          },
        },
      }}
    >
      {/* ヘッダー（固定） */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
        >
          <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
            {title}
          </Typography>
          {badges}
        </Stack>
        {headerActions && <Box sx={{ flexShrink: 0 }}>{headerActions}</Box>}
      </Box>

      {/* コンテンツ（スクロール） */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>{children}</Box>
    </Drawer>
  );
};

/** ドロワー内のセクション見出し（AWSの「概要 / 権限」タブ帯風のシンプルな見出し）。 */
export const DrawerSection: React.FC<{
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, action, children }) => (
  <Box sx={{ mb: 3 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
        {title}
      </Typography>
      {action}
    </Stack>
    <Divider sx={{ mb: 1.5 }} />
    {children}
  </Box>
);

/** キー・バリューの定義リスト（AWSの詳細プロパティ表示風）。 */
export const PropertyRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Stack direction="row" spacing={2} sx={{ py: 0.75 }}>
    <Typography variant="body2" color="text.secondary" sx={{ width: 120, flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Stack>
);

/** 状態バッジ（有効 / 非表示 等）。 */
export const StatusBadge: React.FC<{
  label: string;
  tone?: 'active' | 'muted' | 'info';
}> = ({ label, tone = 'active' }) => {
  const color = tone === 'active' ? 'success' : tone === 'info' ? 'info' : ('default' as const);
  return (
    <Chip
      size="small"
      label={label}
      color={color === 'default' ? undefined : color}
      variant={tone === 'muted' ? 'outlined' : 'filled'}
      sx={{ borderRadius: 1, height: 20, fontSize: 11, fontWeight: 600 }}
    />
  );
};

export default ConsoleDetailDrawer;
